/**
 * Utility functions for array operations
 */

/**
 * Filter an array to contain only unique objects based on a property
 * @param {Array} array - The array to filter
 * @param {string|Function} keyOrFn - The property name or function to extract the unique key
 * @returns {Array} - A new array with unique objects
 */
export const filterUniqueByProperty = (array, keyOrFn) => {
  if (!array || !Array.isArray(array)) return [];
  
  return array.filter((item, index, self) => {
    const key = typeof keyOrFn === 'function' 
      ? keyOrFn(item) 
      : item[keyOrFn];
    
    return index === self.findIndex(obj => {
      const objKey = typeof keyOrFn === 'function' 
        ? keyOrFn(obj) 
        : obj[keyOrFn];
      
      return objKey === key;
    });
  });
};

/**
 * Filter an array to contain only unique objects based on a nested property path
 * @param {Array} array - The array to filter
 * @param {Array<string>} propertyPath - Array representing the path to the nested property
 * @returns {Array} - A new array with unique objects
 */
export const filterUniqueByNestedProperty = (array, propertyPath) => {
  if (!array || !Array.isArray(array)) return [];
  
  const getNestedProperty = (obj, path) => {
    return path.reduce((acc, prop) => acc && acc[prop] ? acc[prop] : undefined, obj);
  };
  
  return array.filter((item, index, self) => {
    const key = getNestedProperty(item, propertyPath);
    
    return index === self.findIndex(obj => {
      const objKey = getNestedProperty(obj, propertyPath);
      return objKey === key;
    });
  });
};

/**
 * Filter an array to contain only unique objects using a Map for better performance
 * @param {Array} array - The array to filter
 * @param {string|Function} keyOrFn - The property name or function to extract the unique key
 * @returns {Array} - A new array with unique objects
 */
export const filterUniqueByPropertyMap = (array, keyOrFn) => {
  if (!array || !Array.isArray(array)) return [];
  
  const uniqueMap = new Map();
  
  array.forEach(item => {
    const key = typeof keyOrFn === 'function' 
      ? keyOrFn(item) 
      : item[keyOrFn];
    
    if (key !== undefined && !uniqueMap.has(key)) {
      uniqueMap.set(key, item);
    }
  });
  
  return Array.from(uniqueMap.values());
};

/**
 * Combine arrays and filter unique objects by a nested property path
 * @param {Array} arrays - Arrays to combine
 * @param {Array<string>} propertyPath - Array representing the path to the nested property
 * @returns {Array} - A new array with unique objects
 */
export const combineAndFilterUnique = (arrays, propertyPath) => {
  if (!arrays || !Array.isArray(arrays)) return [];
  
  const combinedArray = arrays.flat();
  
  const getNestedProperty = (obj, path) => {
    return path.reduce((acc, prop) => acc && acc[prop] ? acc[prop] : undefined, obj);
  };
  
  const uniqueMap = new Map();
  
  combinedArray.forEach(item => {
    const key = getNestedProperty(item, propertyPath);
    
    if (key !== undefined && !uniqueMap.has(key)) {
      uniqueMap.set(key, item);
    }
  });
  
  return Array.from(uniqueMap.values());
}; 